import { useCallback, useMemo, useState, type ComponentType, type ReactNode } from 'react';
import { type EntityPickerSuggestNew } from '@bliks-io/ui';
import { buildSuggestNew } from '../buildCopy';
import type { SuggestionPickEvent } from '../shared/suggestionPickEvent';

/**
 * Props shape that any SuggestionDialog (Team, System, …) must accept. The
 * dialog opens with an initial name and, when submitted, creates an
 * `EntitySuggestion` tied to the active recording or process.
 */
export interface SuggestionDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  initialName: string;
  sourceRecordingId?: string;
  /** Process behind a node-anchored source — cache-seed hint, not wire data. */
  seedProcessId?: string;
  /** Draft-editor host: routes create/link to the draft suggestion routes. */
  draftProcessId?: string;
  onUseExistingEntity?: (entityId: string) => void;
  onSuggestionCreated?: (event: SuggestionPickEvent) => void;
}

/**
 * Props shape of a create dialog (Team, Funktion, System). The dialog creates
 * the entity outright — no suggestion — and reports the new id once the
 * picker's list query can resolve it.
 */
export interface CreateDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  initialName: string;
  onCreated: (id: string) => void;
}

export type PickerSlotMode = 'suggest' | 'create' | 'hidden';

function resolveSlotMode(sourceRecordingId: string | undefined, allowCreate: boolean) {
  if (allowCreate) return 'create';
  return sourceRecordingId ? 'suggest' : 'hidden';
}

interface UsePickerSlotSuggestionsArgs {
  mode: 'single' | 'multi';
  selectedIds: string[];
  /**
   * Source node the suggest-new dialog anchors to. Without it — and without
   * `allowCreate` — the suggest-new affordance stays hidden.
   */
  sourceRecordingId?: string;
  /** Forwarded to the dialog as the create mutation's cache-seed hint. */
  seedProcessId?: string;
  /** Forwarded to the dialog: create/link post to the draft routes. */
  draftProcessId?: string;
  SuggestionDialog: ComponentType<SuggestionDialogProps>;
  /**
   * Host opt-in AND permission — the editor creates instead of suggesting.
   * Wins over a present `sourceRecordingId`: an Org Manager in the editor
   * creates outright while everyone else suggests against the same node.
   */
  allowCreate: boolean;
  CreateDialog: ComponentType<CreateDialogProps>;
}

export interface UsePickerSlotSuggestionsResult {
  mode: PickerSlotMode;
  suggestNew: EntityPickerSuggestNew | undefined;
  renderSuggestDialog: (args: {
    onUseExistingEntity: (next: string[]) => void;
    /**
     * Fired after a suggestion is persisted (`'created'`) or adopted
     * (`'adopted'`). Consumers write `{ type: 'suggestion', id }` into the
     * slot they own for BOTH kinds and typically close the picker (matching
     * the `onUseExistingEntity` UX); withdraw tracking narrows to `'created'`.
     */
    onSuggestionCreated?: (event: SuggestionPickEvent) => void;
  }) => ReactNode;
}

/**
 * Slot-side plumbing for the suggest-new flow. Owns the dialog open/close
 * state and forwards the `initialName` typed into the picker so the dialog
 * can preseed it. Suggestion creation itself is delegated to the dialog,
 * which calls `useEntitySuggestionDialog` and writes the new suggestion's
 * id back into the slot via the picker's normal `onChange` path.
 */
export function usePickerSlotSuggestions({
  mode,
  selectedIds,
  sourceRecordingId,
  seedProcessId,
  draftProcessId,
  SuggestionDialog,
  allowCreate,
  CreateDialog
}: UsePickerSlotSuggestionsArgs): UsePickerSlotSuggestionsResult {
  const [suggestDialogOpen, setSuggestDialogOpen] = useState(false);
  const [suggestQuery, setSuggestQuery] = useState('');

  const slotMode: PickerSlotMode = resolveSlotMode(sourceRecordingId, allowCreate);

  const suggestNew = useMemo<EntityPickerSuggestNew | undefined>(
    () =>
      slotMode === 'hidden'
        ? undefined
        : buildSuggestNew((q) => {
            setSuggestQuery(q);
            setSuggestDialogOpen(true);
          }),
    [slotMode]
  );

  const renderSuggestDialog = useCallback<UsePickerSlotSuggestionsResult['renderSuggestDialog']>(
    ({ onUseExistingEntity, onSuggestionCreated }) => {
      const useEntity = (entityId: string) =>
        onUseExistingEntity(
          mode === 'single' ? [entityId] : Array.from(new Set([...selectedIds, entityId]))
        );
      if (slotMode === 'suggest') {
        return (
          <SuggestionDialog
            open={suggestDialogOpen}
            onOpenChange={setSuggestDialogOpen}
            initialName={suggestQuery}
            sourceRecordingId={sourceRecordingId}
            seedProcessId={seedProcessId}
            draftProcessId={draftProcessId}
            onUseExistingEntity={useEntity}
            onSuggestionCreated={onSuggestionCreated}
          />
        );
      }
      // Mounted only while open: the catalog create dialogs run their member
      // and logo queries unconditionally, while the suggestion dialog above
      // gates its own queries on `open`.
      if (slotMode === 'create' && suggestDialogOpen) {
        return (
          <CreateDialog
            open={suggestDialogOpen}
            onOpenChange={setSuggestDialogOpen}
            initialName={suggestQuery}
            onCreated={useEntity}
          />
        );
      }
      return null;
    },
    [
      slotMode,
      suggestDialogOpen,
      suggestQuery,
      sourceRecordingId,
      seedProcessId,
      draftProcessId,
      SuggestionDialog,
      CreateDialog,
      mode,
      selectedIds
    ]
  );

  return {
    mode: slotMode,
    suggestNew,
    renderSuggestDialog
  };
}
